import { Dispatch } from 'redux';
import { batch } from 'react-redux';
import { ModalsDataTypes } from 'hooks/useModal';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/src/generated';
import { deskingActions } from '../model';
import { defaultCashTerm } from '../constants';
import { AftermarketMenu, BuilderViewType, ProposalMenuId } from '../types';

interface Options {
  terms: number[];
  isCash: boolean;
  proposalMenuId: string;
  dispatch: Dispatch<any>;
  products: ProposalProduct[];
  proposalsProductsIds: string[];
  proposalMenus: Record<ProposalMenuId, AftermarketMenu>;
  handleOpen: (data?: ModalsDataTypes['deskingNotifyAboutRemovedProductsDialog']) => void;
}

interface RemovedProductsOptions {
  products: ProposalProduct[];
  proposalsProductsIds: string[];
  productsOnMenu: ProposalProduct[];
}

// Menu products that are no longer in the list of products available for desking (expired, inactive, etc.)
export function getProductsRemovedFromMenu({ products, productsOnMenu, proposalsProductsIds }: RemovedProductsOptions) {
  const removed = productsOnMenu.reduce<ProposalProduct[]>((acc, product) => {
    if (
      !proposalsProductsIds.includes(product.proposalProductId) &&
      !products.find(p => p.aftermarketProduct?.productName === product.aftermarketProduct?.productName)
    ) {
      acc.push(product);
    }
    return acc;
  }, []);

  return removed.filter(
    (product, index) => removed.findIndex(p => p.proposalProductId === product.proposalProductId) === index
  );
}

export function setMenuDeps({
  terms,
  isCash,
  products,
  dispatch,
  handleOpen,
  proposalMenus,
  proposalMenuId,
  proposalsProductsIds,
}: Options) {
  const financeProgramTerms = isCash ? [defaultCashTerm].map(Number) : terms;

  dispatch(deskingActions.setMenuToBuilder({ id: proposalMenuId, terms: financeProgramTerms.map(String) }));

  const maxTerm = Math.max(...financeProgramTerms);

  batch(() => {
    dispatch(deskingActions.toggleIsEditEquipmentStatus({ status: true }));
    dispatch(deskingActions.setMenuTerm({ term: String(maxTerm), index: financeProgramTerms.lastIndexOf(maxTerm) }));
    dispatch(
      deskingActions.setProductsConfigFromExistingMenu({
        terms: financeProgramTerms.map(String),
        data: proposalMenus[proposalMenuId].menuOptions,
      })
    );
    dispatch(deskingActions.updateMenuBuilderViewType({ type: BuilderViewType.AftermarketMenu }));
  });

  const productsRemovedFromMenu = getProductsRemovedFromMenu({
    products,
    proposalsProductsIds,
    productsOnMenu: proposalMenus[proposalMenuId].menuOptions.flatMap(option => option?.products ?? []),
  });

  if (productsRemovedFromMenu.length) {
    handleOpen({ removedProducts: productsRemovedFromMenu });

    dispatch(
      deskingActions.removeProductsFromMenu({
        ids: productsRemovedFromMenu.map(({ proposalProductId }) => proposalProductId),
      })
    );
  }
}
